"use client";

import { usePathname } from "next/navigation";
import { CTASection } from "@/components/ConversionPage";
import {
  trackSEOAgentBridgeClickedLazy,
  trackSEOAgentBridgeViewedLazy,
  trackSEOIntentCapturedLazy,
} from "@/lib/lazy-analytics-seo-bridge";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";
import { scorePurchaseIntent } from "@/lib/purchase-intent-score";
import { useEffect, useMemo } from "react";
import { safeSessionSet } from "@/lib/browser-storage";

/**
 * Compact above-the-fold revenue CTA for SEO landing pages.
 *
 * App install CTAs are intentionally out of the SEO path until the app
 * experience is ready. This CTA preserves SEO intent and sends users
 * into the web kundali/report flow where checkout attribution exists.
 */
export type InlineAppCTAProps = { language?: "en" | "hi" };

export default function InlineAppCTA({ language = "en" }: InlineAppCTAProps) {
  const pathname = usePathname();
  const config = useMemo(() => getSEOIntentConfigForSource(pathname), [pathname]);
  const sourcePath = pathname || "/";
  const href = buildSEOAgentHref(config, sourcePath);
  const purchaseIntent = useMemo(
    () =>
      scorePurchaseIntent({
        intent: config.intent,
        sourcePath,
        primaryOffer: config.primaryOffer,
      }),
    [config, sourcePath]
  );
  const title =
    language === "hi"
      ? "अपनी कुंडली से सही रिपोर्ट और उपाय देखें"
      : "Get guidance based on your own chart";
  const sub =
    language === "hi"
      ? "पहले जन्म विवरण से चार्ट बनाएं, फिर सही रिपोर्ट, उपाय या पूजा मार्गदर्शन देखें"
      : "Generate your chart first, then see the report, remedy, or puja guidance " +
        "that fits your kundali.";

  useEffect(() => {
    trackSEOAgentBridgeViewedLazy({
      source_path: sourcePath,
      intent: config.intent,
      page_category: config.pageCategory,
      offer_type: config.primaryOffer,
      plan_id: config.reportPlanId,
      purchase_intent_score: purchaseIntent.score,
      purchase_intent_band: purchaseIntent.band,
      purchase_intent_reason: purchaseIntent.reason,
    });
  }, [config, purchaseIntent, sourcePath]);

  function handleClick() {
    try {
      safeSessionSet("seo_entry_intent", config.intent);
      safeSessionSet("seo_entry_path", sourcePath);
      safeSessionSet("seo_entry_page_category", config.pageCategory);
      safeSessionSet("seo_entry_primary_offer", config.primaryOffer);
      safeSessionSet("seo_entry_report_plan_id", config.reportPlanId);
      safeSessionSet("seo_entry_prompt", config.prompt);
      safeSessionSet("seo_entry_purchase_intent_score", String(purchaseIntent.score));
      safeSessionSet("seo_entry_purchase_intent_band", purchaseIntent.band);
      safeSessionSet("seo_entry_purchase_intent_reason", purchaseIntent.reason);
    } catch {
      // Analytics still records the click when storage is unavailable.
    }
    trackSEOAgentBridgeClickedLazy({
      source_path: sourcePath,
      intent: config.intent,
      page_category: config.pageCategory,
      offer_type: config.primaryOffer,
      plan_id: config.reportPlanId,
      target: config.target,
      purchase_intent_score: purchaseIntent.score,
      purchase_intent_band: purchaseIntent.band,
      purchase_intent_reason: purchaseIntent.reason,
    });
    trackSEOIntentCapturedLazy({
      source_path: sourcePath,
      intent: config.intent,
      page_category: config.pageCategory,
      target: config.target,
    });
  }

  return (
    <div className="mt-4 mb-8" onClick={handleClick}>
      <CTASection
        eyebrow={language === "hi" ? "Personalised Web Report" : "Personalised Web Report"}
        title={title}
        subtitle={sub}
        href={href}
        cta={language === "hi" ? "कुंडली बनाएं" : config.ctaLabel}
        compact
      />
    </div>
  );
}
